import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Trash2 } from "lucide-react";
import React from "react";
import { toast } from "sonner";

import {
  createRate,
  deleteRate,
  getRates,
  type RateKind,
  type RateVersion,
} from "@/api/warehouse_ops";
import { DateInput } from "@/components/DateInput";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toastError } from "@/lib/api-errors";
import { fmtDate } from "@/lib/format";
import { Dialog } from "@/lib/scan-ui/overlays";
import { LoadState } from "@/lib/scan-ui/screen";
import {
  fmtRateValue,
  normalizeRateValue,
  rateValueError,
  TARIFF_UNSET_TEXT,
} from "@/lib/warehouse-ops";

import { RefetchError } from "./RefetchError";

export const RATES_QUERY = ["warehouse-rates"] as const;

/**
 * Справочник тарифов и норм склада: что действует сегодня и история версий.
 * Правка — только с правом `warehouse_tariffs` (или админ): новая версия с
 * датой начала, ошибочную — удалить. Старые версии не переписываются.
 */
export function RatesBlock({ canEdit }: { canEdit: boolean }): React.ReactElement {
  const q = useQuery({ queryKey: RATES_QUERY, queryFn: getRates });
  const [open, setOpen] = React.useState<string | null>(null);
  const [adding, setAdding] = React.useState<RateKind | null>(null);
  const [removing, setRemoving] = React.useState<{ kind: RateKind; v: RateVersion } | null>(
    null,
  );
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: RATES_QUERY });
    void qc.invalidateQueries({ queryKey: ["warehouse-ops"] });
  };
  const del = useMutation({
    mutationFn: (id: number) => deleteRate(id),
    onSuccess: () => {
      toast.success("Версия удалена");
      setRemoving(null);
      invalidate();
    },
    onError: toastError("Не удалось удалить версию"),
  });

  return (
    <section className="rounded-lg border bg-white p-3" aria-label="Тарифы и нормы">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold">Тарифы и нормы</h2>
        <span className="text-xs text-muted-foreground">
          {q.data ? `действуют на ${fmtDate(q.data.today)}` : ""}
          {!canEdit ? " · правка — у кого есть право «Тарифы склада: правка»" : ""}
        </span>
      </div>
      {q.data && q.error && <RefetchError error={q.error} onRetry={() => void q.refetch()} />}
      {!q.data ? (
        <LoadState isLoading={q.isLoading} error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[520px] text-left text-sm">
            <thead className="text-xs text-muted-foreground">
              <tr>
                <th className="py-1 pr-2 font-medium">Показатель</th>
                <th className="py-1 pr-2 font-medium">Сейчас</th>
                <th className="py-1 pr-2 font-medium">С даты</th>
                <th className="py-1 font-medium" />
              </tr>
            </thead>
            <tbody>
              {q.data.kinds.map((kind) => (
                <React.Fragment key={kind.key}>
                  <tr className="border-t">
                    <td className="py-1.5 pr-2">
                      <button
                        type="button"
                        className="flex items-center gap-1 text-left"
                        onClick={() => setOpen(open === kind.key ? null : kind.key)}
                        aria-expanded={open === kind.key}
                      >
                        {open === kind.key ? (
                          <ChevronDown className="size-3.5" />
                        ) : (
                          <ChevronRight className="size-3.5" />
                        )}
                        {kind.title}
                        <span className="text-xs text-muted-foreground">· {kind.unit}</span>
                      </button>
                    </td>
                    <td className="py-1.5 pr-2">
                      {kind.current ? (
                        fmtRateValue(kind, kind.current.value)
                      ) : (
                        <span className="font-medium text-amber-700">{TARIFF_UNSET_TEXT}</span>
                      )}
                    </td>
                    <td className="py-1.5 pr-2 text-muted-foreground">
                      {kind.current ? fmtDate(kind.current.valid_from) : ""}
                    </td>
                    <td className="py-1.5 text-right">
                      {canEdit && (
                        <Button variant="outline" size="sm" onClick={() => setAdding(kind)}>
                          Новая версия
                        </Button>
                      )}
                    </td>
                  </tr>
                  {open === kind.key && (
                    <tr>
                      <td colSpan={4} className="pb-2 pl-5">
                        {kind.versions.length === 0 ? (
                          <p className="text-xs text-muted-foreground">Версий нет.</p>
                        ) : (
                          <ul className="space-y-1 text-xs">
                            {kind.versions.map((v) => (
                              <li key={v.id} className="flex flex-wrap items-center gap-2">
                                <span className="font-medium">с {fmtDate(v.valid_from)}</span>
                                <span>{fmtRateValue(kind, v.value)}</span>
                                <span className="text-muted-foreground">
                                  {v.created_by_name}
                                  {v.note ? ` · ${v.note}` : ""}
                                </span>
                                {canEdit && (
                                  <button
                                    type="button"
                                    className="text-rose-600"
                                    aria-label={`Удалить версию с ${fmtDate(v.valid_from)}`}
                                    onClick={() => setRemoving({ kind, v })}
                                  >
                                    <Trash2 className="size-3.5" />
                                  </button>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {adding && (
        <AddVersionDialog
          kind={adding}
          onClose={() => setAdding(null)}
          onSaved={() => {
            setAdding(null);
            setOpen(adding.key);
            invalidate();
          }}
        />
      )}
      {removing && (
        <Dialog
          title="Удалить версию?"
          body={
            <>
              {removing.kind.title}: {fmtRateValue(removing.kind, removing.v.value)} с{" "}
              {fmtDate(removing.v.valid_from)}. Дни с этой даты пересчитаются по предыдущей
              версии (или станут «{TARIFF_UNSET_TEXT}»).
            </>
          }
          confirmLabel="Удалить"
          danger
          confirmBusy={del.isPending}
          onConfirm={() => del.mutate(removing.v.id)}
          onCancel={() => setRemoving(null)}
        />
      )}
    </section>
  );
}

function AddVersionDialog({
  kind,
  onClose,
  onSaved,
}: {
  kind: RateKind;
  onClose: () => void;
  onSaved: () => void;
}): React.ReactElement {
  const [validFrom, setValidFrom] = React.useState("");
  const [dateError, setDateError] = React.useState("");
  const [value, setValue] = React.useState("");
  const [note, setNote] = React.useState("");
  const valueError = rateValueError(value, kind.kind);
  const save = useMutation({
    mutationFn: () =>
      createRate({ key: kind.key, valid_from: validFrom, value: normalizeRateValue(value), note }),
    onSuccess: () => {
      toast.success("Версия сохранена");
      onSaved();
    },
    onError: toastError("Не удалось сохранить версию"),
  });
  const ready = !!validFrom && !dateError && !valueError;
  return (
    <Dialog
      title={`Новая версия: ${kind.title}`}
      body={
        <div className="space-y-2 text-sm text-slate-800">
          <label className="block">
            <span className="text-xs text-muted-foreground">Действует с</span>
            <DateInput
              value={validFrom}
              onChange={(iso, err) => {
                setValidFrom(iso);
                setDateError(err);
              }}
              ariaLabel="Действует с"
            />
          </label>
          <label className="block">
            <span className="text-xs text-muted-foreground">Значение, {kind.unit}</span>
            <Input
              inputMode="decimal"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              aria-label="Значение"
            />
            {value && valueError && <span className="text-xs text-rose-600">{valueError}</span>}
          </label>
          <label className="block">
            <span className="text-xs text-muted-foreground">Примечание</span>
            <Input value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
          </label>
        </div>
      }
      confirmLabel="Сохранить"
      confirmBusy={save.isPending}
      confirmDisabled={!ready}
      onConfirm={() => save.mutate()}
      onCancel={onClose}
    />
  );
}
